<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";

import { toast } from "@/common/utils/toast";
import { base64ToFile } from "@/common/utils/files";

import { employeModelCSV } from "@/common/api/rest/employee";
import {
  evaluationEmployeeList,
  evaluationEmployeeCreate,
  evaluationEmployeeDelete,
} from "@/common/api/rest/evaluation/employee";

import { employeeCreate } from "@/common/api/rest/employee";

import type {
  EmployeeListType,
  EmployeePayloadType,
} from "@/common/types/employee";
import type { EvaluationType } from "@/common/types/evaluation";

import { fileUploadForm, employeeForm, searchEmployee } from "@/components";
import { baseConfirm, baseModal, baseTable } from "@/common/components";

interface SelectItemType {
  employee_id: string;
  name: string;
}

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: EvaluationType | null;
    width?: string;
    showTitle?: boolean;
  }>(),
  {
    width: "100%",
    showTitle: true,
  },
);

const tableHeader = computed(() => [
  { key: "first_name", label: t("common.labels.name") },
]);

const tableData = ref<EmployeeListType[]>([]);

const selected = ref<any>(null);
const selecteds = ref<any[]>([]);
const selectedItem = ref<SelectItemType | null>(null);
const employeeData = ref<EmployeePayloadType | null>(null);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showConfirmDel = ref(false);
const showModalNew = ref(false);

function fetchData() {
  if (!props.data) return;

  evaluationEmployeeList(props.data.account_id, {
    evaluation_id: props.data.evaluation_id,
    limit: limit.value,
    page: page.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function update() {
  emit("update-data");
}

function add() {
  showModalAdd.value = true;
  selectedItem.value = null;
}

function addCancel() {
  showModalAdd.value = false;
  selectedItem.value = null;
}

function addSelect(data: any[]) {
  selecteds.value = data;
}

function addConfirm() {
  if (!props.data || !selecteds.value.length) return;

  showModalAdd.value = false;

  const promises = selecteds.value.map((sl) =>
    evaluationEmployeeCreate(props.data.account_id, {
      evaluation_id: props.data.evaluation_id,
      employee_id: sl.employee_id,
    }),
  );

  Promise.all(promises)
    .then((responses) => {
      responses.forEach((response) => {
        const { success, message } = response;
        if (success) {
          toast({ message: message, type: "success" });
          fetchData();
          update();
        } else {
          toast({ message: message, type: "error" });
        }
      });
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function remove(data: EmployeeListType) {
  selected.value = data;
  showConfirmDel.value = true;
}

function removeCancel() {
  selected.value = null;
  showConfirmDel.value = false;
}

function removeConfirm() {
  if (!props.data || !selected.value) return;

  store.commit("SET_LOADING", true);

  evaluationEmployeeDelete(props.data.account_id, selected.value.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeCancel();
        update();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function newRegister(name: string) {
  showModalNew.value = true;
  showModalAdd.value = false;
  selected.value = null;
  employeeData.value = { name } as any;
}

function newRegisterCancel() {
  showModalNew.value = false;
  selected.value = null;
  employeeData.value = null;
}

function newRegisterConfirm() {
  if (!props.data?.company) return;

  showModalNew.value = false;

  employeeCreate(
    props.data.account_id,
    props.data?.company?.company_id,
    employeeData.value as any,
  )
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        evaluationEmployeeCreate(data.account_id, {
          evaluation_id: props.data.evaluation_id,
          employee_id: data.employee_id,
        })
          .then(() => {
            toast({ message: message, type: "success" });
          })
          .finally(() => {
            fetchData();
            update();
          });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function searchItem(value: string) {
  search.value = value;
  fetchData();
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <template v-if="showTitle">
      <div class="title">
        {{ t("common.labels.employees") }}
      </div>
    </template>

    <div class="content">
      <baseTable
        :header="tableHeader"
        :data="tableData"
        :limit="limit"
        :page="page"
        :pages="pages"
        :total="total"
        :searched="searched"
        :options="true"
        :add="!!data?.company"
        :del="true"
        @change:page="changePage"
        @searching="searchItem"
        @btn:add="add()"
        @btn:del="remove"
      >
        <template #first_name="{ data }">
          <span> {{ data.name }}</span>
        </template>
      </baseTable>
    </div>

    <base-modal
      :show="showModalAdd"
      :title="
        t('common.labels.add', {
          concat: t('common.labels.employee'),
        }).toUpperCase()
      "
      :show-button-confirm="true"
      :button-confirm="t('common.labels.add')"
      :button-close="t('common.labels.cancel')"
      @close="addCancel()"
      @confirm="addConfirm()"
    >
      <template #content>
        <search-employee
          :account-id="data?.account_id"
          :company-id="data?.company?.company_id"
          :add="true"
          :ids="tableData?.map((item) => item.employee_id)"
          @update-data="addSelect"
          @new:register="newRegister"
        />
      </template>
    </base-modal>

    <base-modal
      :show="showModalNew"
      :title="
        t('common.labels.add', {
          concat: t('common.labels.employee'),
        }).toUpperCase()
      "
      :show-button-confirm="true"
      :button-confirm="t('common.labels.add')"
      :button-close="t('common.labels.cancel')"
      @close="newRegisterCancel()"
      @confirm="newRegisterConfirm()"
    >
      <template #content>
        <employee-form
          :data="employeeData"
          @update-data="employeeData = $event"
        />
      </template>
    </base-modal>

    <base-confirm
      :show="showConfirmDel"
      :title="
        t('common.labels.remove', {
          concat: t('common.labels.employee'),
        }).toUpperCase()
      "
      :message="
        t('common.texts.remove_message', {
          concat: selected?.name,
        })
      "
      @cancel="removeCancel"
      @confirm="removeConfirm"
    />
  </section>
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid var(--border);
    padding-block-end: 0.5rem;
  }
}
</style>
